import { type ChangeEvent, type FormEvent, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  AUDIT_MAX_DEPENDENCIES,
  MANIFEST_KINDS,
  MANIFEST_LABELS,
  MANIFEST_PARSERS,
  ManifestParseError,
  detectManifestKind,
} from '@repo/shared';
import type {
  AuditDependency,
  AuditReport,
  AuditStatus,
  ManifestKind,
  SeverityLevel,
} from '@repo/shared';
import { useAudit } from '../features/audit/hooks';
import { ExportJsonButton } from '../components/ExportJsonButton';
import { exportFilename } from '../lib/exportJson';
import styles from './AuditPage.module.css';
import pageStyles from './pages.module.css';
import pkgStyles from './PackagePage.module.css';

// ---- Types ----------------------------------------------------------------

type SortKey = 'status' | 'name';
type SortDir = 'asc' | 'desc';

// ---- Constants ------------------------------------------------------------

const STATUS_ORDER: Record<AuditStatus, number> = {
  vulnerable: 0,
  deprecated: 1,
  ok: 2,
  unknown: 3,
};

const STATUS_LABELS: Record<AuditStatus, string> = {
  vulnerable: 'Vulnerable',
  deprecated: 'Deprecated',
  ok: 'OK',
  unknown: 'Unknown',
};

const STATUS_BADGE_CLASS: Record<AuditStatus, string> = {
  vulnerable: styles.statusVulnerable ?? '',
  deprecated: styles.statusDeprecated ?? '',
  ok: styles.statusOk ?? '',
  unknown: styles.statusUnknown ?? '',
};

const SEVERITY_BADGE_CLASS: Record<SeverityLevel, string> = {
  CRITICAL: pkgStyles.severityCritical ?? '',
  HIGH: pkgStyles.severityHigh ?? '',
  MEDIUM: pkgStyles.severityMedium ?? '',
  LOW: pkgStyles.severityLow ?? '',
  UNKNOWN: pkgStyles.severityUnknown ?? '',
};

const SEVERITY_LABELS: Record<SeverityLevel, string> = {
  CRITICAL: 'Critical',
  HIGH: 'High',
  MEDIUM: 'Medium',
  LOW: 'Low',
  UNKNOWN: 'Unknown',
};

// ---- Component ------------------------------------------------------------

/** Manifest audit page — paste or upload a manifest, get a vulnerability report. */
export function AuditPage() {
  const [content, setContent] = useState('');
  const [filename, setFilename] = useState<string | undefined>(undefined);
  const [kindOverride, setKindOverride] = useState<ManifestKind | 'auto'>('auto');
  const [parseError, setParseError] = useState<string | null>(null);
  const [truncatedFrom, setTruncatedFrom] = useState<number | null>(null);
  const [skippedOpen, setSkippedOpen] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('status');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const audit = useAudit();

  // Pre-flight: parse whenever we have content and no pending/success state so
  // the summary updates live as the user types.
  const preFlight = (() => {
    if (!content.trim()) return null;
    try {
      const kind = kindOverride !== 'auto' ? kindOverride : detectManifestKind(content, filename);
      if (!kind) return null; // can't detect yet — show detection notice below
      const parser = MANIFEST_PARSERS[kind];
      return parser(content);
    } catch {
      return null;
    }
  })();

  function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setFilename(file.name);
    void file.text().then((text) => {
      setContent(text);
      setParseError(null);
      audit.reset();
    });
    // Reset the input so re-uploading the same file triggers onChange
    e.target.value = '';
  }

  function handleKindChange(e: ChangeEvent<HTMLSelectElement>) {
    const v = e.target.value;
    setKindOverride(v === 'auto' ? 'auto' : (v as ManifestKind));
    setParseError(null);
    audit.reset();
  }

  function handleContentChange(e: ChangeEvent<HTMLTextAreaElement>) {
    setContent(e.target.value);
    setFilename(undefined);
    setParseError(null);
    audit.reset();
    setTruncatedFrom(null);
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setParseError(null);
    setTruncatedFrom(null);

    const trimmed = content.trim();
    if (!trimmed) return;

    // 1. Detect format
    let kind: ManifestKind | null;
    if (kindOverride !== 'auto') {
      kind = kindOverride;
    } else {
      kind = detectManifestKind(trimmed, filename);
      if (!kind) {
        setParseError(
          'Could not auto-detect the manifest format. Please choose the format from the dropdown.',
        );
        return;
      }
    }

    // 2. Parse
    let parsed;
    try {
      parsed = MANIFEST_PARSERS[kind](trimmed);
    } catch (err) {
      if (err instanceof ManifestParseError) {
        setParseError(`Could not parse this manifest: ${err.message}`);
      } else {
        setParseError('An unexpected error occurred while parsing the manifest.');
      }
      return;
    }

    // 3. Check for empty result
    if (parsed.dependencies.length === 0) {
      setParseError(
        `No auditable dependencies found in this manifest.${
          parsed.skipped.length > 0
            ? ` ${parsed.skipped.length} entr${parsed.skipped.length === 1 ? 'y was' : 'ies were'} skipped (no registry version to look up).`
            : ''
        }`,
      );
      return;
    }

    // 4. Cap at max
    let deps: AuditDependency[] = parsed.dependencies;
    if (deps.length > AUDIT_MAX_DEPENDENCIES) {
      setTruncatedFrom(deps.length);
      deps = deps.slice(0, AUDIT_MAX_DEPENDENCIES);
    }

    audit.mutate(deps);
  }

  function handleSortColumn(key: SortKey) {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const sortedRows = (() => {
    if (!audit.data) return [];
    return [...audit.data.rows].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'status') {
        cmp = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
        if (cmp === 0) cmp = a.dependency.name.localeCompare(b.dependency.name);
      } else {
        cmp = a.dependency.name.localeCompare(b.dependency.name);
        if (cmp === 0) cmp = STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  })();

  const canSubmit = content.trim().length > 0 && !audit.isPending;
  /** Range entries carry no version until the Worker resolves them. */
  const rangeCount = preFlight?.dependencies.filter((d) => d.version === null).length ?? 0;

  return (
    <div className={pageStyles.page}>
      <h1 className={pageStyles.title}>Manifest Audit</h1>
      <p className={pageStyles.lead}>
        Paste or upload a dependency manifest. PatchProbe checks every pinned dependency for
        vulnerabilities and deprecations; version ranges are simplified to the newest release they
        allow before checking.
      </p>

      {/* ---- Input card ---- */}
      <form onSubmit={handleSubmit}>
        <div className={styles.inputCard}>
          <div>
            <label className={styles.fieldLabel} htmlFor="manifest-textarea">
              Manifest content
            </label>
            <textarea
              id="manifest-textarea"
              className={styles.manifestTextarea}
              value={content}
              onChange={handleContentChange}
              placeholder={
                'Paste a package.json, requirements.txt, go.mod, Cargo.toml, pom.xml, .csproj / packages.config, or Gemfile.lock here…'
              }
              spellCheck={false}
              autoCorrect="off"
              autoCapitalize="off"
            />
          </div>

          <div className={styles.controlRow}>
            <select
              id="manifest-kind"
              className={styles.kindSelect}
              value={kindOverride}
              onChange={handleKindChange}
              aria-label="Manifest format"
            >
              <option value="auto">Auto-detect format</option>
              {MANIFEST_KINDS.map((k) => (
                <option key={k} value={k}>
                  {MANIFEST_LABELS[k]}
                </option>
              ))}
            </select>

            <label className={styles.fileLabel}>
              or upload a file
              <input
                ref={fileInputRef}
                type="file"
                className={styles.fileInput}
                onChange={handleFileChange}
                aria-label="Upload manifest file"
              />
            </label>
            {filename && (
              <span className={pageStyles.muted} style={{ fontSize: '0.8rem' }}>
                {filename}
              </span>
            )}

            <button type="submit" className={styles.submitButton} disabled={!canSubmit}>
              {audit.isPending ? 'Auditing…' : 'Audit'}
            </button>
          </div>

          {/* Inline parse / detect error */}
          {parseError && (
            <p className={styles.inlineError} role="alert">
              {parseError}
            </p>
          )}

          {/* Auto-detect notice when content exists but detection hasn't resolved yet */}
          {!parseError &&
            content.trim() &&
            kindOverride === 'auto' &&
            !preFlight &&
            !audit.data &&
            !audit.isPending && (
              <p className={styles.inlineNotice}>
                Cannot auto-detect the format — choose it from the dropdown above, then click Audit.
              </p>
            )}
        </div>
      </form>

      {/* ---- Truncation notice ---- */}
      {truncatedFrom !== null && (
        <p className={styles.inlineWarning}>
          Manifest contains {truncatedFrom} dependencies — auditing the first{' '}
          {AUDIT_MAX_DEPENDENCIES} (cap reached).
        </p>
      )}

      {/* ---- Pre-flight summary (live, before submit) ---- */}
      {preFlight && !audit.data && !audit.isPending && !audit.isError && (
        <div className={styles.preflight}>
          <p className={styles.preflightSummary}>
            {preFlight.dependencies.length} dependenc
            {preFlight.dependencies.length === 1 ? 'y' : 'ies'} found
            {rangeCount > 0 ? ` (${rangeCount} version range${rangeCount === 1 ? '' : 's'})` : ''}
            {preFlight.skipped.length > 0 ? `, ${preFlight.skipped.length} skipped` : ''}
          </p>
          {rangeCount > 0 && <RangeNotice count={rangeCount} />}
          {preFlight.skipped.length > 0 && (
            <>
              <button
                type="button"
                className={styles.skippedToggle}
                onClick={() => setSkippedOpen((o) => !o)}
                aria-expanded={skippedOpen}
              >
                {skippedOpen ? 'Hide' : 'Show'} skipped entries
              </button>
              {skippedOpen && (
                <ul className={styles.skippedList} aria-label="Skipped dependencies">
                  {preFlight.skipped.map((s, i) => (
                    <li key={i} className={styles.skippedItem}>
                      <span className={styles.skippedName}>{s.name}</span>
                      <span className={styles.skippedReason}>{s.reason}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}

      {/* ---- Loading state ---- */}
      {audit.isPending && (
        <p className={styles.loadingState} role="status">
          Auditing{' '}
          {preFlight ? Math.min(preFlight.dependencies.length, AUDIT_MAX_DEPENDENCIES) : ''}{' '}
          dependenc
          {preFlight && preFlight.dependencies.length === 1 ? 'y' : 'ies'}…
        </p>
      )}

      {/* ---- Mutation error ---- */}
      {audit.isError && (
        <p className={pageStyles.error} role="alert">
          Audit failed: {audit.error instanceof Error ? audit.error.message : 'Unknown error'}
        </p>
      )}

      {/* ---- Results ---- */}
      {audit.data && (
        <AuditResults
          report={audit.data}
          rows={sortedRows}
          sortKey={sortKey}
          sortDir={sortDir}
          onSort={handleSortColumn}
        />
      )}
    </div>
  );
}

// ---- Sub-components -------------------------------------------------------

import type { AuditRow } from '@repo/shared';

/**
 * Tells the user, up front and again in the results, that a range is audited at
 * a single version — not across every version it allows.
 */
function RangeNotice({ count }: { count: number }) {
  return (
    <p className={styles.rangeNotice} role="note">
      <strong>
        {count} dependenc{count === 1 ? 'y uses' : 'ies use'} a version range.
      </strong>{' '}
      PatchProbe audits only the newest published version each range allows — the other versions in
      the range are not analyzed, so a vulnerability that affects only an older version in the range
      will not appear here.
    </p>
  );
}

function AuditResults({
  report,
  rows,
  sortKey,
  sortDir,
  onSort,
}: {
  report: AuditReport;
  rows: AuditRow[];
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (key: SortKey) => void;
}) {
  const { summary } = report;
  const rangeRows = report.rows.filter((r) => r.resolution !== 'exact').length;
  const unresolvedRows = report.rows.filter((r) => r.resolution === 'unresolved').length;

  return (
    <>
      {/* Summary strip */}
      <div className={styles.summaryStrip} aria-label="Audit summary">
        <span className={`${styles.summaryChip} ${styles.chipTotal}`}>{summary.total} total</span>
        {summary.vulnerable > 0 && (
          <span className={`${styles.summaryChip} ${styles.chipVulnerable}`}>
            {summary.vulnerable} vulnerable
          </span>
        )}
        {summary.deprecated > 0 && (
          <span className={`${styles.summaryChip} ${styles.chipDeprecated}`}>
            {summary.deprecated} deprecated
          </span>
        )}
        {summary.ok > 0 && (
          <span className={`${styles.summaryChip} ${styles.chipOk}`}>{summary.ok} ok</span>
        )}
        {summary.unknown > 0 && (
          <span className={`${styles.summaryChip} ${styles.chipUnknown}`}>
            {summary.unknown} unknown
          </span>
        )}
        <ExportJsonButton
          className={styles.exportButton}
          filename={exportFilename('audit', new Date().toISOString().slice(0, 10))}
          kind="audit-report"
          data={report}
        />
      </div>

      {rangeRows > 0 && <RangeNotice count={rangeRows} />}
      {unresolvedRows > 0 && (
        <p className={styles.inlineWarning}>
          {unresolvedRows} range{unresolvedRows === 1 ? '' : 's'} could not be resolved to a
          published version and {unresolvedRows === 1 ? 'was' : 'were'} not audited — check the
          Version column.
        </p>
      )}

      {/* Results table */}
      <div className={styles.tableWrapper}>
        <table className={styles.resultsTable} aria-label="Audit results">
          <thead>
            <tr>
              <th
                className={styles.sortableHeader}
                onClick={() => onSort('name')}
                aria-sort={
                  sortKey === 'name' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
                }
              >
                Package
                {sortKey === 'name' && (
                  <span className={styles.sortIndicator}>{sortDir === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
              <th>Version</th>
              <th
                className={styles.sortableHeader}
                onClick={() => onSort('status')}
                aria-sort={
                  sortKey === 'status' ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
                }
              >
                Status
                {sortKey === 'status' && (
                  <span className={styles.sortIndicator}>{sortDir === 'asc' ? '▲' : '▼'}</span>
                )}
              </th>
              <th>Advisories</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <AuditTableRow
                key={`${row.dependency.system}:${row.dependency.name}:${row.dependency.version}:${i}`}
                row={row}
              />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function AuditTableRow({ row }: { row: AuditRow }) {
  const { dependency, status, resolution, advisories } = row;
  const encodedName = encodeURIComponent(dependency.name);

  return (
    <tr>
      <td className={styles.pkgCell}>
        <Link
          to="/pkg/$system/$name"
          params={{ system: dependency.system, name: encodedName }}
          search={{ v: dependency.version ?? undefined }}
          className={styles.pkgLink}
        >
          {dependency.name}
        </Link>
      </td>
      <td className={styles.versionCell}>
        {dependency.version ?? <span className={pageStyles.muted}>—</span>}
        {resolution !== 'exact' && dependency.requirement && (
          <span
            className={styles.requirementHint}
            title={
              resolution === 'resolved'
                ? `Newest version allowed by ${dependency.requirement}`
                : `Could not resolve ${dependency.requirement} to a published version`
            }
          >
            {resolution === 'resolved' ? 'from ' : 'unresolved: '}
            {dependency.requirement}
          </span>
        )}
      </td>
      <td>
        <span className={`${styles.statusBadge} ${STATUS_BADGE_CLASS[status]}`}>
          {STATUS_LABELS[status]}
        </span>
      </td>
      <td>
        {advisories.length === 0 ? (
          <span className={pageStyles.muted}>—</span>
        ) : (
          <div className={styles.advisoriesCell}>
            {advisories.map((adv) => (
              <div key={adv.id} className={styles.advisoryRef}>
                <Link to="/advisory/$id" params={{ id: adv.id }} className={styles.advisoryIdLink}>
                  {adv.id}
                </Link>
                <span
                  className={`${styles.advisorySeverityBadge} ${SEVERITY_BADGE_CLASS[adv.severity]}`}
                >
                  {SEVERITY_LABELS[adv.severity]}
                </span>
                {adv.fixedVersions.length > 0 && (
                  <span className={styles.advisoryFix}>fix: {adv.fixedVersions[0]}</span>
                )}
              </div>
            ))}
          </div>
        )}
      </td>
    </tr>
  );
}
